import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import Api from '../Auth/Api';
import { decodeJWT } from '../Components/jwtUtils';

// Criando o contexto
const AjustesContext = createContext();

// Hook personalizado para usar o contexto
export const useAjustes = () => useContext(AjustesContext);

// Função auxiliar para converter cor hex para RGB
const hexToRgb = (hex) => {
    // Remover o # se existir
    hex = hex.replace('#', '');
    
    // Converter para RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
};

// Provedor do contexto
export const AjustesProvider = ({ children }) => {
    const [ajustes, setAjustes] = useState({
        titulo: 'Bem vindo!',
        cor_primaria: '#fff',
        cor_secundaria: '#fff',
        logo: null,
        logo_white: null,
        favicon: null,
        diploma: null,
        texto_certificado: null,
        flag: null,
        gestor_pode_cadastrar_editar_usuario: 1,
        gestor_pode_atribuir_cursos: 1,
        gestor_pode_resetar_senha: 1,
        gestor_escopo_edicao: 'responsavel',
        gestor_escopo_reset_senha: 'responsavel',
        gestor_ver_todos_cursos: 1,
        gestor_pode_criar_agendamento: 0,
        rh_pode_criar_agendamento: 0,
        gestor_agenda_todas_unidades: 0,
        rh_agenda_todas_unidades: 0,
        gestor_pode_importar_usuario: 0,
        rh_pode_importar_usuario: 0,
        gestor_pode_exportar_usuario: 1,
        rh_pode_exportar_usuario: 1,
        carregado: false
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Função para aplicar as cores ao CSS
    const aplicarCores = useCallback((primaria, secundaria) => {
        document.documentElement.style.setProperty('--bs-primary', primaria);
        document.documentElement.style.setProperty('--bs-primary-rgb', hexToRgb(primaria));
        document.documentElement.style.setProperty('--bs-secondary', secundaria);
        document.documentElement.style.setProperty('--bs-secondary-rgb', hexToRgb(secundaria));
    }, []);

    // Função para construir URL de imagem
    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) {
            return path; // Already a full URL (external like Imgur)
        }
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);

    // Função para aplicar o favicon
    const aplicarFavicon = useCallback((faviconUrl) => {
        const fullUrl = getImageUrl(faviconUrl);
        if (!fullUrl) return;
        
        let link = document.querySelector("link#favicon");
        if (!link) {
            link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                link.id = 'favicon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
        }
        link.href = fullUrl;
        
        // Também atualiza o apple-touch-icon se existir
        let appleIcon = document.querySelector("link[rel~='apple-touch-icon']");
        if (appleIcon) {
            appleIcon.href = fullUrl;
        }
    }, [getImageUrl]);

    // Função para carregar os ajustes da API.
    // silencioso: recarrega sem a tela de loading do App (que desmontaria a página atual e seus toasts).
    const carregarAjustes = useCallback(async ({ silencioso = false } = {}) => {
        try {
            if (!silencioso) setLoading(true);
            setError(null);

            // Ler empresa_id do JWT para que a rota pública retorne os dados da empresa correta
            let empresaId = 1;
            try {
                const token = JSON.parse(localStorage.getItem('user_token'));
                if (token) {
                    const decoded = decodeJWT(token);
                    if (decoded?.empresa_id) empresaId = decoded.empresa_id;
                }
            } catch (_) { /* mantém empresa_id = 1 */ }

            const response = await Api.get(`public/ajustes?empresa_id=${empresaId}`);
            
            if (response.data) {
                // Definir valores padrão para campos que podem ser nulos
                const dadosAjustes = {
                    ...response.data,
                    cor_primaria: response.data.cor_primaria || '#3a5a6c',
                    cor_secundaria: response.data.cor_secundaria || '#6c8a9c',
                    gestor_pode_cadastrar_editar_usuario: Number(response.data.gestor_pode_cadastrar_editar_usuario ?? 1),
                    gestor_pode_atribuir_cursos: Number(response.data.gestor_pode_atribuir_cursos ?? 1),
                    gestor_pode_resetar_senha: Number(response.data.gestor_pode_resetar_senha ?? 1),
                    gestor_escopo_edicao: response.data.gestor_escopo_edicao || 'responsavel',
                    gestor_escopo_reset_senha: response.data.gestor_escopo_reset_senha || 'responsavel',
                    gestor_ver_todos_cursos: Number(response.data.gestor_ver_todos_cursos ?? 1),
                    gestor_pode_criar_agendamento: Number(response.data.gestor_pode_criar_agendamento ?? 0),
                    rh_pode_criar_agendamento: Number(response.data.rh_pode_criar_agendamento ?? 0),
                    gestor_agenda_todas_unidades: Number(response.data.gestor_agenda_todas_unidades ?? 0),
                    rh_agenda_todas_unidades: Number(response.data.rh_agenda_todas_unidades ?? 0),
                    gestor_pode_importar_usuario: Number(response.data.gestor_pode_importar_usuario ?? 0),
                    rh_pode_importar_usuario: Number(response.data.rh_pode_importar_usuario ?? 0),
                    gestor_pode_exportar_usuario: Number(response.data.gestor_pode_exportar_usuario ?? 1),
                    rh_pode_exportar_usuario: Number(response.data.rh_pode_exportar_usuario ?? 1),
                    carregado: true
                };
                
                setAjustes(dadosAjustes);
                
                // Aplicar as cores ao CSS da página
                aplicarCores(dadosAjustes.cor_primaria, dadosAjustes.cor_secundaria);
                
                // Aplicar o título da página
                if (dadosAjustes.titulo) {
                    document.title = `${dadosAjustes.titulo}`;
                    
                    // Atualizar a meta description
                    const metaDescription = document.querySelector('meta[name="description"]');
                    if (metaDescription) {
                        metaDescription.setAttribute('content', `${dadosAjustes.titulo} - Plataforma de treinamentos e certificações`);
                    }
                }
                
                // Aplicar o favicon
                if (dadosAjustes.favicon) {
                    aplicarFavicon(dadosAjustes.favicon);
                }
            }
            
            setLoading(false);
        } catch (error) {
            console.error("Erro ao carregar ajustes:", error);
            setError("Não foi possível carregar as configurações da plataforma");
            setLoading(false);
        }
    }, [aplicarCores, aplicarFavicon]);

    // Carregar ajustes ao montar o componente
    useEffect(() => {
        carregarAjustes();
    }, [carregarAjustes]);

    // Valor do contexto
    const value = {
        ajustes,
        loading,
        error,
        recarregarAjustes: () => carregarAjustes({ silencioso: true })
    };

    return (
        <AjustesContext.Provider value={value}>
            {children}
        </AjustesContext.Provider>
    );
}; 